import type { ContainerItemDef, ItemDef, ItemInstance } from "../items/types.js";
import { createContainerGrid, type GridState } from "./grid.js";

/**
 * The rules of nesting, as pure functions. `ItemInstance.contents` turned a
 * container in a grid into a subtree, and the server's `applyInventoryOp`, the
 * client's optimistic copy and the UI's drag layer must all answer "does this
 * fit in that box" the same way — so the answers live here, not in `ops.ts`.
 * Nothing mutates; see `items/types.ts` for the rule.
 */

/**
 * How deep a grid may be; top-level grids are depth 0. 2 allows stash → a full
 * backpack's contents → a full medbox inside it, and nothing inside that. The
 * cap is legibility, not memory — and the termination proof for every recursive
 * walk over contents.
 */
export const MAX_GRID_DEPTH = 2;

/**
 * How many grid levels this item brings with it: 0 for no contents, 1 for a box
 * of leaves, 2 for a box holding a box — what `applyInventoryOp` adds to the
 * target's depth against `MAX_GRID_DEPTH`. Measures the WHOLE subtree,
 * unbounded: honest data is already capped.
 */
export function subtreeDepth(item: ItemInstance): number {
  const contents = item.contents;
  if (contents === undefined) return 0;
  let deepest = 0;
  for (const p of contents.items) {
    const d = subtreeDepth(p.item);
    if (d > deepest) deepest = d;
  }
  return deepest + 1;
}

/**
 * Whether `hostDef`'s grid accepts `itemDef`'s kind. An absent filter accepts
 * everything (every rig and backpack). IMMEDIATE HOST ONLY: a filtered box
 * inside an unfiltered pack is legal.
 */
export function containerAccepts(hostDef: ContainerItemDef, itemDef: ItemDef): boolean {
  const filter = hostDef.filter;
  if (filter === undefined) return true;
  return filter.includes(itemDef.kind);
}

/**
 * The grid inside `item`, or a fresh empty one sized to `def` — never `null`,
 * since the missing `contents` key IS the empty grid. The fallback is a FRESH
 * object the caller owns; the non-fallback is the instance's own grid,
 * read-only unless the caller owns the instance.
 */
export function contentsGridFor(def: ContainerItemDef, item: ItemInstance): GridState {
  return item.contents ?? createContainerGrid(def);
}

/**
 * Finds `iid` anywhere in `roots`, nested containers included. READ-ONLY
 * (inspector, tooltip, drag layer); the op path uses `locate` in `ops.ts`,
 * which freshens the host chain. DETERMINISM: roots in order, items in array
 * order, a host reported before its contents; stops at `MAX_GRID_DEPTH`.
 * Returns the instance, its grid depth, and `chain` — host iids outermost
 * inward (a breadcrumb).
 */
export function findNestedItem(
  roots: readonly GridState[],
  iid: string,
): { item: ItemInstance; depth: number; chain: readonly string[] } | null {
  const walk = (
    grid: GridState,
    depth: number,
    chain: readonly string[],
  ): { item: ItemInstance; depth: number; chain: readonly string[] } | null => {
    for (const p of grid.items) {
      if (p.item.iid === iid) return { item: p.item, depth, chain };
    }
    if (depth >= MAX_GRID_DEPTH) return null;
    for (const p of grid.items) {
      const contents = p.item.contents;
      if (contents === undefined) continue;
      const hit = walk(contents, depth + 1, [...chain, p.item.iid]);
      if (hit !== null) return hit;
    }
    return null;
  };

  for (const root of roots) {
    const hit = walk(root, 0, []);
    if (hit !== null) return hit;
  }
  return null;
}
